<template>
  <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent">
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
  <FeedbackModule />
  
  <div class="view-container">
    <router-view v-slot="{ Component }">
      <transition :name="transitionName">
        <component :is="Component" :key="$route.path" class="view-content" />
      </transition>
    </router-view>
  </div>

  <FooterMenu></FooterMenu>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import FeedbackModule from '@/components/FeedbackModule.vue';
import FooterMenu from '@/components/FooterMenu.vue';

const route = useRoute();
const transitionName = ref('slide-left');

// Define route order for directional transitions
const routeOrder = {
  'login': 0,
  'home': 1,
  'match': 1.5,
  'pool-settings': 2,
  'vote-overview': 3,
  'chat': 4,
  'account': 5,
  'settings': 6,
  'privacy': 7
};

watch(() => route.name, (toName, fromName) => {
  if (!fromName) return; // Initial load

  const toIndex = routeOrder[toName] !== undefined ? routeOrder[toName] : 999;
  const fromIndex = routeOrder[fromName] !== undefined ? routeOrder[fromName] : 999;
  
  transitionName.value = toIndex < fromIndex ? 'slide-right' : 'slide-left';
}, { immediate: true });
</script>

<style>
:root {
  /* Apple-like Monochrome & Vibrant Accents */
  --primary-color: #38A3A5; /* Apple Blue */
  --secondary-color: #723D46; /* Apple Purple */
  --accent-color: #FF9500; /* Apple Orange */
  
  --text-main: #1D1D1F;
  --text-secondary: #86868B;
  --text-on-gradient: #FFFFFF;
  
  --bg-gradient: linear-gradient(135deg, #723D46 0%, #38A3A5 100%);
  --card-bg: rgba(255, 255, 255, 0.85);
  --glass-bg: rgba(255, 255, 255, 0.15);
  --border-color: rgba(255, 255, 255, 0.2);
  
  --font: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif;
  
  --radius-s: 8px;
  --radius-m: 12px;
  --radius-l: 20px;
  --radius-xl: 32px;
  
  --shadow-soft: 0 4px 14px 0 rgba(0, 0, 0, 0.05);
  --shadow-strong: 0 8px 30px rgba(0, 0, 0, 0.12);
  
  --mid-width: 400px;
  --max-width: 600px;
  --ultra-max-width: 900px;
}

/* Global Fix for sizing issues */
*, *::before, *::after {
  box-sizing: border-box;
}

body {
  margin: 0;
  background: #F5F5F7;
  overscroll-behavior: contain;
  overflow: hidden;
  font-family: var(--font);
  color: var(--text-main);
  width: 100vw;
  max-width: 100vw;
}

#app {
  font-family: var(--font);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: var(--bg-gradient);
  width: 100vw;
  max-width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
}

@media only screen and (min-width: 600px) {
  #app {
    flex-direction: row-reverse;
  }
}

.view-container {
  position: relative;
  flex-grow: 1;
  width: 100%;
  overflow: hidden;
  height: 100%;
}

.view-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden; 
  -webkit-overflow-scrolling: touch;
  background: var(--bg-gradient);
  background-attachment: fixed;
}

.darken-page {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 100;
  animation: fadeIn 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Global Modern Utilities */
.glass-effect {
  background: var(--glass-bg);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--border-color);
}

.card {
  background: var(--card-bg);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: var(--radius-l);
  box-shadow: var(--shadow-strong);
  padding: 24px;
  color: var(--text-main) !important;
}

.card h1, .card h2, .card h3, .card h4, .card p, .card label {
  color: var(--text-main);
}

.center-msg {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 40px;
  color: white;
  text-align: center;
}

.error {
  color: #FF3B30;
  font-weight: 600;
}

button {
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  font-family: inherit;
  outline: none;
}

button:active {
  transform: scale(0.96);
}

/* Slide Transitions */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.1, 1);
  will-change: transform, opacity;
}

/* Slide Left: New View enters from Right, Old View leaves to Left */
.slide-left-enter-from {
  transform: translateX(100%);
  opacity: 0.5;
}
.slide-left-leave-to {
  transform: translateX(-30%);
  opacity: 0;
}

/* Slide Right: New View enters from Left, Old View leaves to Right */
.slide-right-enter-from {
  transform: translateX(-100%);
  opacity: 0.5;
}
.slide-right-leave-to {
  transform: translateX(30%);
  opacity: 0;
}

/* Make sure the entering view is on top during transition */
.slide-left-enter-active,
.slide-right-enter-active {
  z-index: 2;
}
.slide-left-leave-active,
.slide-right-leave-active {
  z-index: 1;
}

#footer {
  z-index: 100;
}
</style>
